// Clean, print-friendly SVG diagrams for the 8.5x11 cheat sheets.
// Each component draws on a transparent / light background using dark ink,
// the set accent `color`, and brand gold (#F5A524). viewBox is ~680x300.
const INK = "#3F3F46";
const SOFT = "#A1A1AA";
const GOLD = "#F5A524";
const FONT = "'Outfit', system-ui, sans-serif";

function Lbl({ x, y, children, size = 15, weight = 700, fill = INK, anchor = "middle" }) {
  return (
    <text x={x} y={y} fontFamily={FONT} fontSize={size} fontWeight={weight} fill={fill} textAnchor={anchor}>
      {children}
    </text>
  );
}

// ---- Aperture: wide vs narrow opening ----
function ApertureRing({ cx, inner, color }) {
  const blades = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i + Math.PI / 6;
    blades.push(
      <line key={i} x1={cx + Math.cos(a) * inner} y1={150 + Math.sin(a) * inner}
        x2={cx + Math.cos(a) * 76} y2={150 + Math.sin(a) * 76} stroke={SOFT} strokeWidth="2" />
    );
  }
  return (
    <g>
      <circle cx={cx} cy="150" r="78" fill="none" stroke={INK} strokeWidth="3" />
      {blades}
      <circle cx={cx} cy="150" r={inner} fill={color} fillOpacity="0.18" stroke={color} strokeWidth="3" />
    </g>
  );
}
export function ApertureIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <ApertureRing cx={180} inner={56} color={color} />
      <ApertureRing cx={500} inner={16} color={color} />
      <Lbl x="180" y="262" fill={color}>f/1.8 — WIDE</Lbl>
      <Lbl x="180" y="284" size="12" weight="500" fill={SOFT}>more light · blurry background</Lbl>
      <Lbl x="500" y="262" fill={color}>f/16 — NARROW</Lbl>
      <Lbl x="500" y="284" size="12" weight="500" fill={SOFT}>less light · everything sharp</Lbl>
      <line x1="300" y1="150" x2="380" y2="150" stroke={SOFT} strokeWidth="2" strokeDasharray="5 5" />
      <path d="M372 144 L384 150 L372 156" fill="none" stroke={SOFT} strokeWidth="2" />
    </svg>
  );
}

// ---- Shutter speed: freeze vs blur ----
export function ShutterIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="40" y="60" width="260" height="150" rx="10" fill="none" stroke={INK} strokeWidth="2.5" />
      <circle cx="170" cy="135" r="26" fill={color} />
      <Lbl x="170" y="40" fill={color}>1/1000s · FREEZE</Lbl>
      <Lbl x="170" y="238" size="12" weight="500" fill={SOFT}>sharp, crisp action</Lbl>

      <rect x="380" y="60" width="260" height="150" rx="10" fill="none" stroke={INK} strokeWidth="2.5" />
      {[0, 1, 2, 3, 4].map((i) => (
        <circle key={i} cx={460 + i * 22} cy="135" r={26 - i * 3} fill={color} fillOpacity={0.18 + i * 0.16} />
      ))}
      <Lbl x="510" y="40" fill={color}>1/15s · BLUR</Lbl>
      <Lbl x="510" y="238" size="12" weight="500" fill={SOFT}>motion streaks (use a tripod)</Lbl>
    </svg>
  );
}

// ---- ISO: clean to noisy ----
export function IsoIllo({ color }) {
  const dots = [];
  for (let i = 0; i < 220; i++) {
    const x = 60 + Math.random() * 560;
    const density = (x - 60) / 560;
    if (Math.random() < density * density) dots.push(<circle key={i} cx={x} cy={90 + Math.random() * 90} r="1.6" fill={INK} fillOpacity="0.6" />);
  }
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="isoGrad" x1="0" x2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.22" />
          <stop offset="1" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <rect x="60" y="70" width="560" height="130" rx="10" fill="url(#isoGrad)" stroke={INK} strokeWidth="2" />
      {dots}
      <Lbl x="130" y="240" fill={color}>ISO 100 · CLEAN</Lbl>
      <Lbl x="540" y="240" fill={color}>ISO 6400 · NOISY</Lbl>
      <Lbl x="340" y="50" size="13" fill={SOFT}>raise ISO only after aperture &amp; shutter are maxed</Lbl>
    </svg>
  );
}

// ---- White balance: kelvin scale ----
export function WhiteBalanceIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="wbGrad" x1="0" x2="1">
          <stop offset="0" stopColor="#3B82F6" />
          <stop offset="0.5" stopColor="#F4F4F5" />
          <stop offset="1" stopColor="#F59E0B" />
        </linearGradient>
      </defs>
      <rect x="60" y="100" width="560" height="64" rx="12" fill="url(#wbGrad)" stroke={INK} strokeWidth="2" />
      {[["3200K", 130, "Tungsten"], ["5500K", 340, "Daylight"], ["7500K", 550, "Shade"]].map(([k, x, n]) => (
        <g key={k}>
          <line x1={x} y1="92" x2={x} y2="172" stroke={INK} strokeWidth="2" />
          <Lbl x={x} y="200" fill={INK}>{k}</Lbl>
          <Lbl x={x} y="220" size="11" weight="500" fill={SOFT}>{n}</Lbl>
        </g>
      ))}
      <Lbl x="150" y="80" size="13" fill="#2563EB">COOL · BLUE</Lbl>
      <Lbl x="540" y="80" size="13" fill="#D97706">WARM · ORANGE</Lbl>
    </svg>
  );
}

// ---- Exposure triangle ----
export function ExposureTriangleIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <polygon points="340,40 560,250 120,250" fill={color} fillOpacity="0.08" stroke={color} strokeWidth="3" strokeLinejoin="round" />
      <circle cx="340" cy="40" r="30" fill="#fff" stroke={INK} strokeWidth="2" />
      <circle cx="120" cy="250" r="30" fill="#fff" stroke={INK} strokeWidth="2" />
      <circle cx="560" cy="250" r="30" fill="#fff" stroke={INK} strokeWidth="2" />
      <Lbl x="340" y="46" size="13">f/</Lbl>
      <Lbl x="340" y="14" size="12" fill={SOFT}>APERTURE</Lbl>
      <Lbl x="120" y="256" size="12">1/s</Lbl>
      <Lbl x="120" y="295" size="12" fill={SOFT}>SHUTTER</Lbl>
      <Lbl x="560" y="256" size="12">ISO</Lbl>
      <Lbl x="560" y="295" size="12" fill={SOFT}>ISO</Lbl>
      <Lbl x="340" y="185" size="15" fill={color}>EXPOSURE</Lbl>
      <Lbl x="340" y="208" size="11" weight="500" fill={SOFT}>change one, compensate another</Lbl>
    </svg>
  );
}

// ---- Depth of field ----
export function DepthOfFieldIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <Lbl x="180" y="36" fill={color}>SHALLOW · f/2</Lbl>
      <circle cx="90" cy="120" r="20" fill={SOFT} fillOpacity="0.35" />
      <circle cx="180" cy="120" r="26" fill={color} />
      <circle cx="275" cy="120" r="20" fill={SOFT} fillOpacity="0.35" />
      <Lbl x="180" y="178" size="11" weight="500" fill={SOFT}>only the subject is sharp</Lbl>

      <Lbl x="500" y="36" fill={color}>DEEP · f/11</Lbl>
      <circle cx="410" cy="120" r="20" fill={color} fillOpacity="0.85" />
      <circle cx="500" cy="120" r="26" fill={color} />
      <circle cx="595" cy="120" r="20" fill={color} fillOpacity="0.85" />
      <Lbl x="500" y="178" size="11" weight="500" fill={SOFT}>front to back is sharp</Lbl>

      <line x1="40" y1="220" x2="640" y2="220" stroke={INK} strokeWidth="1.5" />
      <Lbl x="340" y="252" size="12" fill={SOFT}>wider aperture + closer subject + longer lens = more blur</Lbl>
    </svg>
  );
}

// ---- Rule-of-thirds grid (composition / rule-of-thirds) ----
export function ThirdsIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="120" y="30" width="440" height="240" rx="8" fill="none" stroke={INK} strokeWidth="2.5" />
      {[1, 2].map((i) => (
        <line key={`v${i}`} x1={120 + (440 / 3) * i} y1="30" x2={120 + (440 / 3) * i} y2="270" stroke={SOFT} strokeWidth="1.5" />
      ))}
      {[1, 2].map((i) => (
        <line key={`h${i}`} x1="120" y1={30 + (240 / 3) * i} x2="560" y2={30 + (240 / 3) * i} stroke={SOFT} strokeWidth="1.5" />
      ))}
      {[[267, 110], [413, 110], [267, 190], [413, 190]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="6" fill={GOLD} />
      ))}
      <circle cx="267" cy="110" r="22" fill={color} />
      <Lbl x="340" y="294" size="12" fill={SOFT}>place key subjects on the intersections, not the center</Lbl>
    </svg>
  );
}

// ---- Histogram ----
export function HistogramIllo({ color }) {
  const bars = [];
  for (let i = 0; i < 40; i++) {
    const t = i / 39;
    const h = 120 * Math.exp(-Math.pow((t - 0.5) * 3.2, 2)) + 8;
    bars.push(<rect key={i} x={70 + i * 13.5} y={200 - h} width="10" height={h} fill={color} fillOpacity="0.7" />);
  }
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <line x1="60" y1="200" x2="620" y2="200" stroke={INK} strokeWidth="2" />
      {bars}
      <Lbl x="80" y="232" size="12" anchor="start" fill={SOFT}>BLACKS</Lbl>
      <Lbl x="340" y="232" size="12" fill={SOFT}>MIDTONES</Lbl>
      <Lbl x="600" y="232" size="12" anchor="end" fill={SOFT}>WHITES</Lbl>
      <Lbl x="340" y="40" size="13" fill={color}>data touching an edge = clipped (lost) detail</Lbl>
    </svg>
  );
}

// ---- Manual mode dial ----
export function ManualModeIllo({ color }) {
  const marks = ["M", "A", "S", "P", "Auto"];
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <circle cx="200" cy="150" r="100" fill="#fff" stroke={INK} strokeWidth="3" />
      {marks.map((m, i) => {
        const a = -Math.PI / 2 + (i * (2 * Math.PI)) / marks.length;
        const x = 200 + Math.cos(a) * 70, y = 150 + Math.sin(a) * 70 + 5;
        return <Lbl key={m} x={x} y={y} size="16" fill={m === "M" ? color : SOFT}>{m}</Lbl>;
      })}
      <line x1="200" y1="150" x2="200" y2="64" stroke={color} strokeWidth="5" strokeLinecap="round" />
      <circle cx="200" cy="150" r="8" fill={color} />
      {["Aperture", "Shutter", "ISO"].map((c, i) => (
        <g key={c}>
          <rect x="360" y={70 + i * 60} width="240" height="44" rx="22" fill={color} fillOpacity="0.12" stroke={color} strokeWidth="1.5" />
          <Lbl x="480" y={98 + i * 60} size="16" fill={INK}>{c} — you choose</Lbl>
        </g>
      ))}
    </svg>
  );
}

// ---- RAW vs JPEG ----
export function RawJpegIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      {[0, 1, 2].map((i) => (
        <rect key={i} x={90 + i * 10} y={70 + i * 10} width="180" height="120" rx="8" fill={color} fillOpacity={0.18 - i * 0.05} stroke={color} strokeWidth="2" />
      ))}
      <Lbl x="180" y="232" fill={color}>RAW</Lbl>
      <Lbl x="180" y="254" size="11" weight="500" fill={SOFT}>all data · max edit latitude</Lbl>

      <rect x="430" y="90" width="170" height="110" rx="8" fill={SOFT} fillOpacity="0.2" stroke={INK} strokeWidth="2" />
      <Lbl x="515" y="232" fill={INK}>JPEG</Lbl>
      <Lbl x="515" y="254" size="11" weight="500" fill={SOFT}>baked in · smaller · ready to share</Lbl>
    </svg>
  );
}

// ---- Metering modes ----
export function MeteringIllo({ color }) {
  const frame = (x, label) => (
    <g>
      <rect x={x} y="60" width="150" height="120" rx="8" fill="none" stroke={INK} strokeWidth="2.5" />
      <Lbl x={x + 75} y="208" size="12" fill={color}>{label}</Lbl>
    </g>
  );
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      {frame(40, "EVALUATIVE")}
      <rect x="48" y="68" width="134" height="104" fill={color} fillOpacity="0.18" />
      {frame(265, "CENTER")}
      <circle cx="340" cy="120" r="46" fill={color} fillOpacity="0.25" />
      {frame(490, "SPOT")}
      <circle cx="565" cy="120" r="14" fill={color} />
      <Lbl x="340" y="248" size="12" fill={SOFT}>spot = the camera reads only the tiny center area</Lbl>
    </svg>
  );
}

// ---- Focus modes AF-S vs AF-C ----
export function FocusModesIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="40" y="60" width="260" height="150" rx="10" fill="none" stroke={INK} strokeWidth="2.5" />
      <rect x="140" y="110" width="60" height="50" fill="none" stroke={color} strokeWidth="3" />
      <Lbl x="170" y="40" fill={color}>AF-S · ONE SHOT</Lbl>
      <Lbl x="170" y="238" size="11" weight="500" fill={SOFT}>still subjects — locks once</Lbl>

      <rect x="380" y="60" width="260" height="150" rx="10" fill="none" stroke={INK} strokeWidth="2.5" />
      {[0, 1, 2].map((i) => (
        <rect key={i} x={420 + i * 60} y={100 + i * 10} width="56" height="48" fill="none" stroke={color} strokeWidth="3" strokeOpacity={0.4 + i * 0.3} />
      ))}
      <Lbl x="510" y="40" fill={color}>AF-C · CONTINUOUS</Lbl>
      <Lbl x="510" y="238" size="11" weight="500" fill={SOFT}>moving subjects — tracks &amp; refocuses</Lbl>
    </svg>
  );
}

// ---- Focal length & crop factor ----
export function CropFactorIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="160" y="50" width="360" height="200" rx="6" fill="none" stroke={INK} strokeWidth="2.5" />
      <Lbl x="340" y="40" size="13" fill={INK}>Full Frame (1.0x)</Lbl>
      <rect x="220" y="90" width="240" height="120" rx="4" fill={color} fillOpacity="0.1" stroke={color} strokeWidth="2.5" strokeDasharray="6 4" />
      <Lbl x="340" y="155" size="13" fill={color}>APS-C crop (1.5x)</Lbl>
      <Lbl x="340" y="280" size="12" fill={SOFT}>a 50mm lens frames like ~75mm on an APS-C body</Lbl>
    </svg>
  );
}

// ---- Flash basics: direct vs bounce ----
export function FlashIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <line x1="40" y1="60" x2="640" y2="60" stroke={SOFT} strokeWidth="3" />
      <Lbl x="340" y="48" size="11" fill={SOFT}>CEILING</Lbl>
      <rect x="150" y="150" width="70" height="46" rx="6" fill="#fff" stroke={INK} strokeWidth="2.5" />
      <rect x="170" y="138" width="30" height="14" rx="3" fill={GOLD} />
      <path d="M195 138 L260 70" stroke={GOLD} strokeWidth="3" />
      <path d="M260 70 L300 175" stroke={GOLD} strokeWidth="3" strokeDasharray="6 5" />
      <circle cx="320" cy="200" r="26" fill={color} />
      <Lbl x="320" y="252" size="12" fill={color}>BOUNCE = soft, flattering light</Lbl>
      <path d="M470 175 L590 175" stroke={INK} strokeWidth="3" />
      <circle cx="600" cy="175" r="22" fill={color} fillOpacity="0.5" />
      <Lbl x="540" y="220" size="11" fill={SOFT}>direct = harsh</Lbl>
    </svg>
  );
}

// ---- Leading lines: converging lines to a subject ----
export function LeadingLinesIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="60" y="30" width="560" height="240" rx="8" fill={`${color}08`} stroke={INK} strokeWidth="2" />
      {[[60, 270], [200, 270], [620, 270], [480, 270]].map(([x, y], i) => (
        <line key={i} x1={x} y1={y} x2="340" y2="120" stroke={SOFT} strokeWidth="2.5" />
      ))}
      <line x1="60" y1="120" x2="620" y2="120" stroke={SOFT} strokeWidth="1.5" strokeDasharray="5 5" />
      <circle cx="340" cy="120" r="24" fill={color} />
      <circle cx="340" cy="120" r="34" fill="none" stroke={GOLD} strokeWidth="2.5" />
      <Lbl x="340" y="295" size="12" fill={SOFT}>lines from the corners pull the eye to your subject</Lbl>
    </svg>
  );
}

// ---- Minimalism: one tiny subject, lots of clean space ----
export function MinimalismIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="120" y="30" width="440" height="240" rx="8" fill={`${color}10`} stroke={INK} strokeWidth="2.5" />
      <circle cx="470" cy="90" r="18" fill={color} />
      <Lbl x="340" y="295" size="12" fill={SOFT}>one small subject · large clean space · 1–2 colors</Lbl>
    </svg>
  );
}

// ---- Blue hour: city skyline against deep-blue sky ----
export function BlueHourIllo() {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="bhSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1E3A8A" />
          <stop offset="0.7" stopColor="#3B82F6" />
          <stop offset="1" stopColor="#F59E0B" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <rect x="60" y="30" width="560" height="210" rx="8" fill="url(#bhSky)" stroke={INK} strokeWidth="2" />
      {[[90, 150, 60, 90], [160, 120, 50, 120], [220, 170, 70, 70], [300, 100, 60, 140], [370, 150, 55, 90], [430, 130, 65, 110], [505, 160, 60, 80]].map(([x, y, w, h], i) => (
        <g key={i}>
          <rect x={x} y={y} width={w} height={h} fill="#0F172A" />
          {[0, 1, 2].map((r) => [0, 1].map((c) => <rect key={`${r}-${c}`} x={x + 8 + c * 22} y={y + 12 + r * 24} width="10" height="12" fill={GOLD} fillOpacity="0.85" />))}
        </g>
      ))}
      <Lbl x="340" y="265" size="13" fill={INK}>~20–30 min window</Lbl>
      <Lbl x="340" y="292" size="12" fill={SOFT}>tripod · long exposure · city lights vs deep-blue sky</Lbl>
    </svg>
  );
}

// ---- Long exposure: silky streaks + timer ----
export function LongExposureIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <circle cx="140" cy="120" r="56" fill="#fff" stroke={INK} strokeWidth="3" />
      <line x1="140" y1="120" x2="140" y2="78" stroke={color} strokeWidth="4" strokeLinecap="round" />
      <line x1="140" y1="120" x2="172" y2="120" stroke={color} strokeWidth="4" strokeLinecap="round" />
      <circle cx="140" cy="120" r="6" fill={color} />
      <Lbl x="140" y="210" size="13" fill={color}>slow shutter</Lbl>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path key={i} d={`M260 ${70 + i * 22} q 130 ${i % 2 ? 18 : -18} 280 0`} fill="none" stroke={color} strokeWidth="3" strokeOpacity={0.35 + i * 0.1} strokeLinecap="round" />
      ))}
      <Lbl x="400" y="232" size="12" fill={SOFT}>tripod + ND filter = silky water &amp; streaking clouds</Lbl>
    </svg>
  );
}

// ---- Lens aberrations: chromatic fringing + vignette ----
export function LensAberrationsIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <Lbl x="180" y="40" fill={color}>CHROMATIC FRINGE</Lbl>
      <rect x="86" y="80" width="100" height="120" fill="#EF4444" fillOpacity="0.8" />
      <rect x="94" y="80" width="100" height="120" fill="#22C55E" fillOpacity="0.7" />
      <rect x="102" y="80" width="100" height="120" fill="#3B82F6" fillOpacity="0.7" />
      <Lbl x="148" y="228" size="11" weight="500" fill={SOFT}>color edges — fix in RAW</Lbl>

      <Lbl x="500" y="40" fill={color}>VIGNETTE</Lbl>
      <defs>
        <radialGradient id="vig" cx="50%" cy="50%" r="60%">
          <stop offset="0.45" stopColor="#fff" />
          <stop offset="1" stopColor="#18181B" />
        </radialGradient>
      </defs>
      <rect x="420" y="80" width="180" height="120" rx="6" fill="url(#vig)" stroke={INK} strokeWidth="1.5" />
      <Lbl x="510" y="228" size="11" weight="500" fill={SOFT}>dark corners — correct or use</Lbl>
      <Lbl x="340" y="270" size="12" fill={SOFT}>stop down 1–2 stops to sharpen corners</Lbl>
    </svg>
  );
}

// ---- Framing: shoot through an arch/doorway ----
export function FramingIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="60" y="30" width="560" height="240" rx="8" fill={`${color}10`} stroke={INK} strokeWidth="2" />
      <path d="M210 250 L210 110 a130 80 0 0 1 260 0 L470 250 Z" fill="#27272A" />
      <path d="M240 250 L240 120 a100 60 0 0 1 200 0 L440 250 Z" fill="#fff" />
      <circle cx="340" cy="175" r="26" fill={color} />
      <Lbl x="340" y="295" size="12" fill={SOFT}>frame the subject through arches, windows or branches</Lbl>
    </svg>
  );
}

// ---- Symmetry & patterns: mirrored + repeating shapes ----
export function SymmetryIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <line x1="340" y1="30" x2="340" y2="240" stroke={GOLD} strokeWidth="2" strokeDasharray="6 5" />
      {[0, 1, 2].map((row) => [0, 1, 2].map((c) => {
        const cx = 110 + c * 95, cy = 70 + row * 60;
        const odd = row === 1 && c === 1;
        return <circle key={`l${row}-${c}`} cx={cx} cy={cy} r="20" fill={odd ? GOLD : color} fillOpacity={odd ? 1 : 0.7} />;
      }))}
      {[0, 1, 2].map((row) => [0, 1, 2].map((c) => {
        const cx = 570 - c * 95, cy = 70 + row * 60;
        return <circle key={`r${row}-${c}`} cx={cx} cy={cy} r="20" fill={color} fillOpacity="0.7" />;
      }))}
      <Lbl x="340" y="275" size="12" fill={SOFT}>center for symmetry · break the pattern for a focal point</Lbl>
    </svg>
  );
}

// ---- Negative space: tiny subject, vast empty area ----
export function NegativeSpaceIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="120" y="30" width="440" height="240" rx="8" fill={`${color}10`} stroke={INK} strokeWidth="2.5" />
      <circle cx="180" cy="210" r="22" fill={color} />
      <path d="M205 205 a30 30 0 0 1 70 -6" fill="none" stroke={SOFT} strokeWidth="2" strokeDasharray="4 4" />
      <path d="M268 196 l8 3 l-3 8" fill="none" stroke={SOFT} strokeWidth="2" />
      <Lbl x="395" y="294" size="12" fill={SOFT}>leave space in the direction the subject faces / moves</Lbl>
    </svg>
  );
}

// ---- Bokeh: sharp subject + creamy out-of-focus orbs ----
export function BokehIllo({ color }) {
  const orbs = [[120, 90, 34], [210, 60, 22], [180, 160, 40], [90, 200, 26], [250, 130, 18], [150, 240, 16]];
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      {orbs.map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill={color} fillOpacity="0.22" stroke={color} strokeOpacity="0.3" strokeWidth="2" />
      ))}
      <circle cx="470" cy="150" r="60" fill={color} />
      <circle cx="470" cy="150" r="72" fill="none" stroke={INK} strokeWidth="2.5" />
      <Lbl x="470" y="245" size="13" fill={color}>SHARP SUBJECT</Lbl>
      <Lbl x="180" y="290" size="12" fill={SOFT}>wide aperture + distance = dreamy orbs</Lbl>
    </svg>
  );
}

// ---- Panning: sharp subject, streaked background ----
export function PanningIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <line key={i} x1="60" y1={70 + i * 22} x2="620" y2={70 + i * 22} stroke={SOFT} strokeWidth="3" strokeOpacity="0.4" strokeDasharray="40 18" />
      ))}
      <g>
        <ellipse cx="340" cy="160" rx="70" ry="34" fill={color} />
        <circle cx="305" cy="190" r="16" fill={INK} />
        <circle cx="378" cy="190" r="16" fill={INK} />
      </g>
      <path d="M210 160 l-26 0 M196 148 l-22 0 M196 172 l-22 0" stroke={color} strokeWidth="4" strokeLinecap="round" strokeOpacity="0.6" />
      <Lbl x="340" y="252" size="12" fill={SOFT}>slow shutter (1/30–1/125s) · follow the subject smoothly</Lbl>
    </svg>
  );
}

// ---- Silhouettes: black subject against bright sky ----
export function SilhouetteIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="silSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F59E0B" />
          <stop offset="1" stopColor="#FDE68A" />
        </linearGradient>
      </defs>
      <rect x="60" y="30" width="560" height="210" rx="8" fill="url(#silSky)" stroke={INK} strokeWidth="2" />
      <circle cx="470" cy="100" r="40" fill="#fff" fillOpacity="0.85" />
      <line x1="60" y1="240" x2="620" y2="240" stroke={INK} strokeWidth="3" />
      <path d="M300 240 C300 195 318 185 318 165 a22 22 0 1 1 44 0 c0 20 18 30 18 75 Z" fill="#18181B" />
      <Lbl x="340" y="292" size="12" fill={SOFT}>meter for the bright background · subject goes black</Lbl>
    </svg>
  );
}

// ---- Reflections: subject mirrored across a water line ----
export function ReflectionsIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <polygon points="340,60 400,150 280,150" fill={color} />
      <rect x="320" y="60" width="40" height="90" fill={color} fillOpacity="0.9" transform="translate(0,0)" />
      <line x1="60" y1="155" x2="620" y2="155" stroke="#3B82F6" strokeWidth="3" />
      <g opacity="0.45">
        <polygon points="340,250 400,160 280,160" fill={color} />
      </g>
      {[0, 1, 2, 3].map((i) => (
        <line key={i} x1={140 + i * 110} y1={185 + i * 12} x2={240 + i * 110} y2={185 + i * 12} stroke="#3B82F6" strokeWidth="2" strokeOpacity="0.5" />
      ))}
      <Lbl x="340" y="290" size="12" fill={SOFT}>get low to the surface · calm water mirrors best</Lbl>
    </svg>
  );
}

// ---- Sunny 16: sun + aperture-by-light reminder ----
export function Sunny16Illo({ color }) {
  const cols = [["f/16", "Sun"], ["f/11", "Haze"], ["f/8", "Cloud"], ["f/5.6", "Dark"], ["f/4", "Shade"]];
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <circle cx="120" cy="90" r="40" fill={GOLD} />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const a = (i * Math.PI) / 4;
        return <line key={i} x1={120 + Math.cos(a) * 52} y1={90 + Math.sin(a) * 52} x2={120 + Math.cos(a) * 70} y2={90 + Math.sin(a) * 70} stroke={GOLD} strokeWidth="5" strokeLinecap="round" />;
      })}
      <Lbl x="120" y="170" size="15" fill={INK}>Shutter = 1/ISO</Lbl>
      {cols.map(([f, label], i) => {
        const x = 270 + i * 82;
        return (
          <g key={f}>
            <circle cx={x} cy="120" r={30 - i * 3.5} fill="none" stroke={color} strokeWidth="3" />
            <circle cx={x} cy="120" r={(30 - i * 3.5) * 0.45} fill={color} />
            <Lbl x={x} y="180" size="17" fill={color}>{f}</Lbl>
            <Lbl x={x} y="205" size="12" fill={SOFT}>{label}</Lbl>
          </g>
        );
      })}
      <Lbl x="430" y="265" size="13" fill={INK}>Brighter light → smaller aperture (bigger f-number)</Lbl>
    </svg>
  );
}

// ---- Generic branded default (used by sets without a custom diagram yet) ----
export function DefaultIllo({ color }) {
  return (
    <svg viewBox="0 0 680 300" width="100%" preserveAspectRatio="xMidYMid meet">
      <rect x="170" y="40" width="340" height="220" rx="10" fill="none" stroke={INK} strokeWidth="2.5" />
      {[1, 2].map((i) => (
        <line key={`v${i}`} x1={170 + (340 / 3) * i} y1="40" x2={170 + (340 / 3) * i} y2="260" stroke={SOFT} strokeWidth="1.2" strokeOpacity="0.7" />
      ))}
      {[1, 2].map((i) => (
        <line key={`h${i}`} x1="170" y1={40 + (220 / 3) * i} x2="510" y2={40 + (220 / 3) * i} stroke={SOFT} strokeWidth="1.2" strokeOpacity="0.7" />
      ))}
      <circle cx="340" cy="150" r="44" fill="none" stroke={color} strokeWidth="4" />
      <circle cx="340" cy="150" r="16" fill={color} />
      <path d="M300 150 a40 40 0 0 1 80 0" fill="none" stroke={GOLD} strokeWidth="3" />
    </svg>
  );
}

const REGISTRY = {
  aperture: ApertureIllo,
  "shutter-speed": ShutterIllo,
  iso: IsoIllo,
  "white-balance": WhiteBalanceIllo,
  "exposure-triangle": ExposureTriangleIllo,
  "depth-of-field": DepthOfFieldIllo,
  "composition-basics": ThirdsIllo,
  "rule-of-thirds": ThirdsIllo,
  histogram: HistogramIllo,
  "manual-mode": ManualModeIllo,
  "raw-vs-jpeg": RawJpegIllo,
  "metering-modes": MeteringIllo,
  "focus-modes": FocusModesIllo,
  "focal-length-crop": CropFactorIllo,
  "flash-basics": FlashIllo,
  "leading-lines": LeadingLinesIllo,
  minimalism: MinimalismIllo,
  "blue-hour": BlueHourIllo,
  "long-exposure": LongExposureIllo,
  "lens-aberrations": LensAberrationsIllo,
  framing: FramingIllo,
  "symmetry-patterns": SymmetryIllo,
  "negative-space": NegativeSpaceIllo,
  bokeh: BokehIllo,
  panning: PanningIllo,
  silhouettes: SilhouetteIllo,
  reflections: ReflectionsIllo,
  "sunny-16-film": Sunny16Illo,
};

export function getIllustration(cardId) {
  return REGISTRY[cardId] || DefaultIllo;
}
